import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { Icon, NonIdealState, Spinner } from '@blueprintjs/core';
import { useAppSelector, useWindowWidth } from 'hooks';

import { ForwardedRef, forwardRef, useRef } from 'react';
import { ClubsResults } from 'store/results';
import { ClimberCategory, ResultCategory } from '../../constants';
import { BoulderRoutesResults, LeadRoutesResults } from './RoutesResults';
import styles from './styles.module.scss';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';

const shownRouteNumbersSelector = (category: ResultCategory) =>
  createSelector(
    (state: RootState) => state.results.routeNumbers,

    (routeNumbers) =>
      category === 'clubs'
        ? {
            lead: [],
            boulder: [],
          }
        : {
            boulder: routeNumbers?.boulder?.[category] || [],
            lead: routeNumbers?.lead?.[category] || [],
          },
  );

export default forwardRef(ResultsTable);

function ResultsTable(
  {
    loading,
    category,
  }: {
    loading: boolean;
    category: ResultCategory;
  },
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const results = useAppSelector((state) => state.results);

  // const showLoading = useDelayedShowLoading(loading);
  const showLoading = loading;

  const [displayedResults, setDisplayedResults] = useState<
    Results | ClubsResults
  >();

  const routeNumbers = useAppSelector(shownRouteNumbersSelector(category));

  const headerRef = useRef<HTMLDivElement>(null);

  function onTableXScroll(e: React.UIEvent<HTMLDivElement>) {
    if (headerRef.current) {
      headerRef.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
    }
  }

  // const tableRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayedResults(category === 'clubs' ? undefined : []);

    setTimeout(() => {
      setDisplayedResults(
        category === 'clubs' ? results.clubs : results.perCategory[category],
      );
    }, 10);
  }, [category, results]);

  const noResults =
    !displayedResults || (category !== 'clubs' && !displayedResults.length);

  if (noResults) {
    return (
      <NonIdealState
        className={styles.emptyState}
        icon={showLoading ? <Spinner /> : 'mountain'}
        title={showLoading ? 'Getting results' : 'No results yet'}
        description={showLoading ? undefined : 'Go climb some more!'}
      />
    );
  }

  if (category === 'clubs') {
    return <ClubsTable results={displayedResults as ClubsResults} />;
  }

  return (
    <>
      <HeaderRow routeNumbers={routeNumbers} ref={headerRef} />

      <div className={styles.container} onScroll={onTableXScroll}>
        <div className={styles.results}>
          {(displayedResults as Results).map((climberResult, index) => {
            return (
              <ClimberRow
                ref={
                  index === (displayedResults as Results).length - 1
                    ? ref
                    : undefined
                }
                key={climberResult.climberId}
                climberResult={climberResult}
                is2NRow={index % 2 === 1}
                routeNumbers={routeNumbers}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}

function ClubsTable({ results }: { results?: ClubsResults }) {
  if (!results || Array.isArray(results) || !Object.keys(results).length) {
    return (
      <NonIdealState
        className={styles.emptyState}
        icon={'mountain'}
        title={'No results yet'}
        description={'Go climb some more!'}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.clubsResults}>
        <div className={classNames(styles.headerCell, styles.emptyCell)} />
        <div className={styles.headerCell}>Position</div>
        <div className={classNames(styles.headerCell, styles.stickyClubName)}>
          Club name
        </div>
        <div className={styles.headerCell}>Total points</div>

        {Object.values(ClimberCategory).map((cat) => (
          <div key={cat} className={styles.headerCell}>
            {cat} points
          </div>
        ))}

        <div className={classNames(styles.headerCell, styles.emptyCell)} />

        {Object.entries(results)
          .sort(([_1, s1], [_2, s2]) => s2.total - s1.total)
          .map(([club, clubResult], index) => {
            const is2NRow = index % 2 === 1;
            return (
              <React.Fragment key={club}>
                <div
                  className={classNames(
                    styles.emptyCell,
                    is2NRow && styles.alternateRow,
                  )}
                />
                <div className={is2NRow && styles.alternateRow}>
                  {index + 1}.
                </div>
                <div
                  className={classNames(
                    styles.stickyClubName,
                    is2NRow && styles.alternateRow,
                  )}
                >
                  {club}
                </div>
                <div className={is2NRow && styles.alternateRow}>
                  {clubResult.total}
                </div>

                {Object.values(ClimberCategory).map((cat) => {
                  return (
                    <div className={is2NRow && styles.alternateRow} key={cat}>
                      {clubResult.perCategory[cat] || '-'}
                    </div>
                  );
                })}

                <div
                  className={classNames(
                    styles.emptyCell,
                    is2NRow && styles.alternateRow,
                  )}
                />
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
}

const ClimberRow = forwardRef(BaseClimberRow);

function BaseClimberRow(
  {
    is2NRow,
    climberResult,
    routeNumbers,
  }: {
    is2NRow: boolean;
    climberResult: ClimberResult;
    routeNumbers: { lead: number[]; boulder: number[] };
  },
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  return (
    <>
      <div
        className={classNames(is2NRow && styles.alternateRow, styles.emptyCell)}
      />

      <div
        className={classNames(
          is2NRow && styles.alternateRow,
          styles.bold,
          styles.stickyPos,
        )}
      >
        {climberResult.position ? climberResult.position + '.' : '-'}
      </div>

      <div
        ref={ref}
        className={classNames(
          styles.avatar,
          styles.stickyName,
          is2NRow && styles.alternateRow,
        )}
      >
        <div>
          <Icon size={15} icon="person" />
          <span>{climberResult.climberNumber}</span>
        </div>

        <div>{climberResult.climberName}</div>
      </div>

      <div
        className={classNames(styles.country, is2NRow && styles.alternateRow)}
      >
        <span>{climberResult.climberCountry}</span>
      </div>
      <div className={classNames(styles.club, is2NRow && styles.alternateRow)}>
        <span>{climberResult.climberClub}</span>
      </div>

      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.totalPoints}
      </div>
      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.boulderPoints}
      </div>
      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.leadPoints}
      </div>
      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.boulderTops}
      </div>
      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.boulderZones}
      </div>
      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.boulderTopAttempts}
      </div>
      <div className={classNames(is2NRow && styles.alternateRow)}>
        {climberResult.boulderZoneAttempts}
      </div>

      <BoulderRoutesResults
        is2NRow={is2NRow}
        routeResults={climberResult.boulderRouteResults}
        routeNumbers={routeNumbers.boulder}
      />
      <LeadRoutesResults
        is2NRow={is2NRow}
        routeResults={climberResult.leadRouteResults}
        routeNumbers={routeNumbers.lead}
      />
      <div
        className={classNames(is2NRow && styles.alternateRow, styles.emptyCell)}
      />
    </>
  );
}

const HeaderRow = forwardRef(function PlainHeaderRow(
  { routeNumbers }: { routeNumbers: { lead: number[]; boulder: number[] } },
  ref: ForwardedRef<HTMLDivElement>,
) {
  const windowWith = useWindowWidth();

  return (
    <div className={classNames(styles.headerRow)}>
      <div className={classNames(styles.headerScroll)} ref={ref}>
        <div className={classNames(styles.headerCell, styles.emptyCell)} />
        <div className={classNames(styles.headerCell, styles.stickyPos)}>
          {windowWith > 400 ? 'Position' : 'Pos.'}
        </div>
        <div className={classNames(styles.headerCell, styles.stickyName)}>
          Climber
        </div>
        <div className={styles.headerCell}>Country</div>
        <div className={styles.headerCell}>Club</div>
        <div className={styles.headerCell}>Points</div>
        <div className={styles.headerCell}>Boulder pts.</div>
        <div className={styles.headerCell}>Lead pts.</div>
        <div className={styles.headerCell}>Boulder Tops</div>
        <div className={styles.headerCell}>Boulder Zones</div>
        <div className={styles.headerCell}>
          {windowWith > 400 ? 'Top attempts' : 'Top attem.'}
        </div>
        <div className={styles.headerCell}>
          {windowWith > 400 ? 'Zone attempts' : 'Zone attem.'}
        </div>
        <div className={classNames(styles.headerCell, styles.scoresHeader)}>
          <span>Boulder routes</span>
          <div className={styles.headerRouteNumbers}>
            {routeNumbers.boulder.map((nr) => (
              <span key={nr}>{nr}</span>
            ))}
          </div>
        </div>
        <div className={classNames(styles.headerCell, styles.scoresHeader)}>
          <span>Lead routes</span>
          <div className={styles.headerRouteNumbers}>
            {routeNumbers.lead.map((nr) => (
              <span key={nr}>{nr}</span>
            ))}
          </div>
        </div>
        <div className={classNames(styles.headerCell, styles.emptyCell)} />
      </div>
    </div>
  );
});

// function useDelayedShowLoading(loading: boolean) {
//   const LOADING_SHOW_DELAY = 1000;

//   const [showLoading, setShowLoading] = useState(false);

//   useEffect(() => {
//     let timeOut: number | undefined;

//     if (loading) {
//       timeOut = window.setTimeout(
//         () => setShowLoading(true),
//         LOADING_SHOW_DELAY,
//       );
//     } else {
//       setShowLoading(false);
//       if (timeOut) {
//         clearTimeout(timeOut);
//       }
//     }

//     return () => {
//       if (timeOut) {
//         clearTimeout(timeOut);
//       }
//     };
//   }, [loading]);

//   return showLoading;
// }
